import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatError = (detail, fallback) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return fallback;
};

const inputCls =
  "w-full border-b border-white/20 bg-transparent py-4 text-base text-[#F2F2F2] placeholder:text-[#555] outline-none transition-colors duration-300 focus:border-[#FF3333]";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const { t } = useLanguage();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t("contact.toastOk"));
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail, t("contact.toastErr")));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="mx-auto max-w-7xl px-6 py-32 md:px-12 md:py-48">
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
        <div>
          <span className="text-sm uppercase tracking-[0.3em] text-[#FF3333]">{t("contact.label")}</span>
          <h2 className="mt-4 font-syne font-extrabold uppercase tracking-tighter text-[#F2F2F2]">
            <MaskedLine innerClassName="text-4xl sm:text-5xl lg:text-6xl">{t("contact.h1")}</MaskedLine>
            <MaskedLine delay={0.12} innerClassName="text-4xl sm:text-5xl lg:text-6xl">
              {t("contact.h2")} <span className="text-[#FF3333]">{t("contact.h2accent")}</span>
            </MaskedLine>
          </h2>
          <FadeUp delay={0.2}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-[#A3A3A3] md:text-lg">
              {t("contact.text")}
            </p>
            <p className="mt-6 text-sm uppercase tracking-[0.25em] text-[#A3A3A3]">
              {t("contact.responseA")} <span className="text-[#FF3333]">{t("contact.responseB")}</span>
            </p>
          </FadeUp>
        </div>

        <FadeUp delay={0.25}>
          <form data-testid="contact-form" onSubmit={submit} className="flex flex-col gap-8">
            <div>
              <label htmlFor="contact-name" className="text-xs uppercase tracking-[0.25em] text-[#A3A3A3]">
                {t("contact.fName")}
              </label>
              <input
                id="contact-name"
                data-testid="contact-name-input"
                required
                minLength={2}
                value={form.name}
                onChange={set("name")}
                placeholder={t("contact.pName")}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="text-xs uppercase tracking-[0.25em] text-[#A3A3A3]">
                {t("contact.fEmail")}
              </label>
              <input
                id="contact-email"
                data-testid="contact-email-input"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                placeholder={t("contact.pEmail")}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="contact-message" className="text-xs uppercase tracking-[0.25em] text-[#A3A3A3]">
                {t("contact.fMessage")}
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                required
                minLength={10}
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder={t("contact.pMessage")}
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="group mt-2 flex w-fit items-center gap-3 rounded-full bg-[#FF3333] px-10 py-4 text-sm font-semibold text-white transition-transform duration-300 hover:scale-105 disabled:opacity-60"
            >
              {sending ? (
                <>
                  {t("contact.sending")} <Loader2 className="h-4 w-4 animate-spin" />
                </>
              ) : (
                <>
                  {t("contact.submit")}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </>
              )}
            </button>
          </form>
        </FadeUp>
      </div>
    </section>
  );
};
